import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Phone, Video, MoreVertical, ArrowLeft, Info } from 'lucide-react';
import { useChat } from '../../context/ChatContext';
import { useAuth } from '../../context/AuthContext';
import MessageBubble from './MessageBubble';
import TypingIndicator from './TypingIndicator';
import VoiceRecorder from './VoiceRecorder';
import { EmojiButton } from './EmojiPicker';
import { useToast } from '../../hooks/use-toast';

const ChatWindow = ({ conversation, onBack }) => {
  const { user } = useAuth();
  const { 
    messages, 
    typingUsers, 
    fetchMessages, 
    sendMessage, 
    sendVoiceMessage, 
    sendTypingIndicator, 
    markMessageRead 
  } = useChat();
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const { toast } = useToast();

  const conversationMessages = messages[conversation?.id] || [];
  const participantName = conversation?.participant_info?.display_name || 
                         conversation?.participant_info?.username || 'User';
  const receiverId = conversation?.participants?.find(p => p !== user?.id);
  const isTyping = typingUsers[conversation?.id]?.length > 0;

  // Fetch messages when conversation changes
  useEffect(() => {
    if (conversation?.id) {
      fetchMessages(conversation.id);
    }
  }, [conversation?.id, fetchMessages]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationMessages]);

  // Mark messages as read
  useEffect(() => {
    if (conversationMessages.length > 0) {
      const unreadMessages = conversationMessages.filter(
        msg => !msg.is_read && msg.sender_id !== user?.id
      );
      unreadMessages.forEach(msg => markMessageRead(msg.id));
    }
  }, [conversationMessages, user?.id, markMessageRead]);

  // Handle typing indicator
  const handleTyping = useCallback(() => {
    if (conversation?.id) {
      sendTypingIndicator(conversation.id, true);
      
      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Set timeout to stop typing indicator
      typingTimeoutRef.current = setTimeout(() => {
        sendTypingIndicator(conversation.id, false);
      }, 2000);
    }
  }, [conversation?.id, sendTypingIndicator]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || !receiverId || sending) return;

    setSending(true);
    const messageContent = inputValue.trim();
    setInputValue('');

    try {
      await sendMessage(receiverId, messageContent);
      // Refetch messages to show the new message
      setTimeout(() => fetchMessages(conversation.id), 500);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to send message. Please try again.',
      });
      setInputValue(messageContent); // Restore the message
    } finally {
      setSending(false);
    }
  };

  const handleSendVoice = async (audioBlob) => {
    if (!receiverId) return;

    try {
      await sendVoiceMessage(receiverId, audioBlob);
      setTimeout(() => fetchMessages(conversation.id), 500);
      toast({
        title: 'Voice message sent',
        description: 'Your voice message was sent successfully.',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to send voice message. Please try again.',
      });
    }
  };

  const handleEmojiSelect = (emoji) => {
    setInputValue(prev => prev + emoji);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!conversation) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-950">
        <div className="text-center text-gray-500">
          <MessageIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <h3 className="text-xl font-medium">Select a conversation</h3>
          <p className="text-sm mt-2">Choose a conversation from the sidebar to start chatting</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full bg-gray-950">
      {/* Chat Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 bg-gray-900">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="md:hidden p-2 rounded-lg hover:bg-gray-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          
          <div className="relative">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
              {participantName.charAt(0).toUpperCase()}
            </div>
            {conversation?.participant_info?.is_online && (
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-gray-900 rounded-full"></span>
            )}
          </div>
          
          <div>
            <h3 className="font-medium text-white">{participantName}</h3>
            <p className="text-xs text-gray-500">
              {conversation?.participant_info?.is_online ? 'Online' : 'Offline'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button className="p-2 rounded-lg hover:bg-gray-800 transition-colors text-gray-400 hover:text-white">
            <Phone className="w-5 h-5" />
          </button>
          <button className="p-2 rounded-lg hover:bg-gray-800 transition-colors text-gray-400 hover:text-white">
            <Video className="w-5 h-5" />
          </button>
          <button className="p-2 rounded-lg hover:bg-gray-800 transition-colors text-gray-400 hover:text-white">
            <Info className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {conversationMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-2xl font-bold mb-4">
              {participantName.charAt(0).toUpperCase()}
            </div>
            <p className="text-lg">Start a conversation with {participantName}</p>
            <p className="text-sm mt-1">Send a message to begin chatting</p>
          </div>
        ) : (
          <>
            {conversationMessages.map((msg) => (
              <MessageBubble 
                key={msg.id} 
                message={msg} 
                isOwn={msg.sender_id === user?.id} 
              />
            ))}
            {isTyping && <TypingIndicator username={participantName} />}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="px-4 py-3 border-t border-gray-800 bg-gray-900">
        <div className="flex items-center gap-2">
          {/* Emoji Button */}
          <EmojiButton onSelect={handleEmojiSelect} />
          
          {/* Voice Recorder */}
          <VoiceRecorder onSend={handleSendVoice} onCancel={() => {}} />
          
          {/* Text Input */}
          <div className="flex-1 relative">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                handleTyping();
              }}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-full text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          {/* Send Button */}
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || sending}
            className={`p-2.5 rounded-full transition-all ${
              inputValue.trim() && !sending
                ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:opacity-90'
                : 'bg-gray-800 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper component for empty state
const MessageIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

export default ChatWindow;
